import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export function useValidationRules(workspaceId: string, stage?: string) {
  return useQuery({
    queryKey: ['validation-rules', workspaceId, stage],
    queryFn: async () => {
      let query = supabase
        .from('validation_rules_v2')
        .select('*')
        .eq('workspace_id', workspaceId)
        .eq('is_active', true);

      if (stage) {
        query = query.or(`trigger_stage.eq.${stage},trigger_stage.is.null`);
      }

      const { data, error } = await query.order('execution_order', { ascending: true });

      if (error) throw error;
      return data;
    },
    enabled: !!workspaceId
  });
}

export function useCreateValidationRule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (rule: any) => {
      const { data, error } = await supabase
        .from('validation_rules_v2')
        .insert(rule)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['validation-rules'] });
      toast.success('Validation rule created');
    },
    onError: (error: Error) => {
      toast.error('Failed to create rule: ' + error.message);
    }
  });
}

export function useUpdateValidationRule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: any }) => {
      const { data, error } = await supabase
        .from('validation_rules_v2')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['validation-rules'] });
      toast.success('Validation rule updated');
    },
    onError: (error: Error) => {
      toast.error('Failed to update rule: ' + error.message);
    }
  });
}

export function useRunValidation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: {
      billId: string;
      workspaceId: string;
      billData: Record<string, any>;
      stage?: string;
    }) => {
      const { data, error } = await supabase.functions.invoke('validation-rules-engine', {
        body: input
      });

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['validation-results'] });
      if (data.allPassed) {
        toast.success('All validations passed');
      } else {
        toast.warning('Some validations failed');
      }
    },
    onError: (error: Error) => {
      toast.error('Validation failed: ' + error.message);
    }
  });
}

export function useValidationResults(billId: string) {
  return useQuery({
    queryKey: ['validation-results', billId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('validation_results')
        .select('*, validation_rules_v2(*)')
        .eq('bill_id', billId)
        .order('validated_at', { ascending: false });

      if (error) throw error;
      return data;
    },
    enabled: !!billId
  });
}
